import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useFullscreen } from '../context/FullscreenContext';
import { Package, Users, Truck, BarChart3, Maximize2, Minimize2, Gamepad2 } from 'lucide-react';
import SpaceShooterGame from '../modules/game/components/SpaceShooterGame';
import GameMenu from '../modules/game/components/GameMenu';

/* ── Styles injected via style tag ── */
const cosmicStyles = `
@keyframes twinkle {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
}
@keyframes pulse-glow {
  0%, 100% { opacity: 0.12; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.05); }
}
@keyframes flyby {
  0%   { transform: translate(0, 0); opacity: 0; }
  5%   { opacity: 1; }
  100% { transform: translate(calc(90vw), -70vh); opacity: 0; }
}
@keyframes flyby2 {
  0%   { transform: translate(0, 0); opacity: 0; }
  5%   { opacity: 1; }
  100% { transform: translate(calc(85vw), 50vh); opacity: 0; }
}
@keyframes sun-pulse {
  0%, 100% { opacity: 0.8; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}
@keyframes bird-fly {
  0%   { transform: translateX(-20vw); opacity: 0; }
  5%   { opacity: 1; }
  95%  { opacity: 1; }
  100% { transform: translateX(110vw); opacity: 0; }
}
.cartoon-wing {
  transform-origin: 30px 48px;
  animation: wing-flap 0.4s ease-in-out infinite;
}
@keyframes wing-flap {
  0%, 100% { transform: scaleY(1) rotate(-5deg); }
  50% { transform: scaleY(-0.6) rotate(10deg); }
}
.cosmic-star {
  position: absolute;
  border-radius: 50%;
  background: white;
}
.cosmic-nebula {
  position: absolute;
  border-radius: 50%;
}
.cosmic-grid {
  position: absolute;
  inset: 0;
  opacity: 0.03;
  background-image: linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px);
  background-size: 60px 60px;
}
.cosmic-spaceship {
  position: absolute;
  left: 5%;
  bottom: 15%;
  font-size: 28px;
  pointer-events: none;
  z-index: 1;
  animation: flyby 6s ease-in-out 2s infinite;
  filter: drop-shadow(0 0 8px rgba(255,200,50,0.5));
}
.cosmic-spaceship-alt {
  position: absolute;
  left: 5%;
  top: 8%;
  font-size: 24px;
  pointer-events: none;
  z-index: 1;
  animation: flyby2 7s ease-in-out 0s infinite;
  filter: drop-shadow(0 0 6px rgba(100,200,255,0.5));
}
`;

const starSpecs = Array.from({ length: 120 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 2.5 + 1,
  delay: Math.random() * 5,
  dur: Math.random() * 3 + 2,
}));

// Single chubby cartoon bird

const nebulaSpecs = [
  { left: 10, top: 20, size: 400, color: 'rgba(99, 102, 241, 0.12)' },
  { left: 65, top: 10, size: 350, color: 'rgba(168, 85, 247, 0.10)' },
  { left: 30, top: 60, size: 380, color: 'rgba(6, 182, 212, 0.08)' },
  { left: 75, top: 55, size: 300, color: 'rgba(236, 72, 153, 0.07)' },
];

const DashboardPage = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContent, setShowContent] = useState(true);
  const [activeGame, setActiveGame] = useState(null);
  const isNight = theme === 'dark';

  useEffect(() => {
    fetch('/api/dashboard/stats')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          setStats(data.data);
        } else {
          setError(data.message);
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const cardClass = isNight
    ? "bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.12] shadow-xl p-6 hover:bg-white/[0.10] transition-all duration-300"
    : "bg-white/70 backdrop-blur-md rounded-2xl border border-white/80 shadow-lg p-6 hover:bg-white/80 transition-all duration-300";

  const bgGradient = isNight
    ? 'linear-gradient(135deg, #0a0e27 0%, #111335 30%, #0d1530 60%, #080c22 100%)'
    : 'linear-gradient(180deg, #4facfe 0%, #87ceeb 35%, #b8e1fc 55%, #d4edff 75%, #f0f8ff 100%)';

  /* ── Background decorations ── */
  const NightDecorations = () => (
    <>
      {nebulaSpecs.map((n, i) => (
        <div key={`n${i}`} className="cosmic-nebula"
          style={{
            left: `${n.left}%`,
            top: `${n.top}%`,
            width: n.size,
            height: n.size,
            background: `radial-gradient(circle, ${n.color} 0%, transparent 70%)`,
            animation: `pulse-glow ${5 + i * 2}s ease-in-out ${i * 1.5}s infinite`,
          }}
        />
      ))}
      {starSpecs.map(s => (
        <div key={s.id} className="cosmic-star"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            opacity: 0.2,
            animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}
      <div className="cosmic-grid" />
      <div className="cosmic-spaceship">🚀</div>
    </>
  );

  const DayDecorations = () => (
    <>
      {/* Sun */}
      <div className="absolute"
        style={{
          top: '6%',
          right: '12%',
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'radial-gradient(circle, #fff9c4 0%, #ffd54f 40%, #ffb300 100%)',
          boxShadow: '0 0 60px rgba(255,213,79,0.4), 0 0 120px rgba(255,213,79,0.2)',
          animation: 'sun-pulse 4s ease-in-out infinite',
        }}
      />
      {/* Single chubby cartoon bird */}
      <div className="absolute"
        style={{
          top: '18%',
          left: 0,
          animation: 'bird-fly 12s ease-in-out 0s infinite',
          pointerEvents: 'none',
        }}
      >
        <svg width="80" height="60" viewBox="0 0 120 90" xmlns="http://www.w3.org/2000/svg">
          {/* Body - chubby oval */}
          <ellipse cx="55" cy="58" rx="38" ry="28" fill="#f87171" />
          {/* Belly */}
          <ellipse cx="52" cy="64" rx="28" ry="18" fill="#fecaca" />
          {/* Head */}
          <circle cx="78" cy="40" r="18" fill="#f87171" />
          {/* White eye */}
          <circle cx="83" cy="37" r="8" fill="white" />
          {/* Pupil */}
          <circle cx="85" cy="36" r="4" fill="#1e293b" />
          {/* Eye shine */}
          <circle cx="87" cy="34" r="1.5" fill="white" />
          {/* Beak */}
          <polygon points="95,42 108,44 95,48" fill="#f59e0b" />
          {/* Wing (drawn separately for flap animation) */}
          <ellipse cx="40" cy="48" rx="20" ry="10" fill="#ef4444" className="cartoon-wing" />
          {/* Tail */}
          <polygon points="18,58 5,46 12,62" fill="#ef4444" />
          {/* Feet */}
          <line x1="45" y1="84" x2="40" y2="90" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="55" y1="84" x2="58" y2="90" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </div>
    </>
  );

  const PageWrapper = ({ children, className = '' }) => (
    <div
      className={`relative min-h-screen -m-3 -mt-3 md:-m-8 overflow-hidden ${className}`}
      style={{ background: bgGradient }}
    >
      {isNight ? <NightDecorations /> : <DayDecorations />}
      {children}
    </div>
  );

  if (loading) {
    return (
      <>
        <style>{cosmicStyles}</style>
        <PageWrapper className="flex items-center justify-center">
          <div className={`relative z-10 animate-spin rounded-full h-12 w-12 border-b-2 ${isNight ? 'border-indigo-400' : 'border-blue-600'}`} />
        </PageWrapper>
      </>
    );
  }

  if (error) {
    return (
      <>
        <style>{cosmicStyles}</style>
        <PageWrapper className="flex items-center justify-center">
          <div className="relative z-10 text-center" style={{ color: isNight ? '#cbd5e1' : '#475569' }}>
            <p>{t('dashboard.load_error')}</p>
            <p className="text-sm mt-1" style={{ color: isNight ? '#64748b' : '#94a3b8' }}>{error}</p>
          </div>
        </PageWrapper>
      </>
    );
  }

  if (!stats) return null;

  const { products, employees, suppliers, product_trend } = stats;
  const maxTrendCount = Math.max(...product_trend.map(d => d.count), 1);

  return (
    <>
      <style>{cosmicStyles}</style>
      <PageWrapper>
        <div className="relative z-10 space-y-6 p-6">
          {/* Top bar: fullscreen + content toggle buttons */}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowContent(false)}
              className={`p-2.5 rounded-xl transition-all duration-200 border shadow-lg backdrop-blur-sm hover:scale-110 ${
                isNight
                  ? 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20 hover:text-white'
                  : 'bg-white/80 border-slate-300 text-slate-600 hover:bg-white hover:text-slate-900'
              }`}
              title={'Game'}
            >
              <Gamepad2 size={18} />
            </button>
            <button
              onClick={toggleFullscreen}
              className={`p-2.5 rounded-xl transition-all duration-200 border shadow-lg backdrop-blur-sm hover:scale-110 ${
                isNight
                  ? 'bg-white/10 border-white/20 text-white/70 hover:bg-white/20 hover:text-white'
                  : 'bg-white/80 border-slate-300 text-slate-600 hover:bg-white hover:text-slate-900'
              }`}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          </div>
          {showContent ? (
          <div className="space-y-6">
          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={cardClass}>
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl ${isNight ? 'bg-blue-500/20 backdrop-blur-sm border border-blue-400/20' : 'bg-white/80 border border-slate-200'}`}>
                  <Package className={isNight ? 'w-7 h-7 text-blue-300' : 'w-7 h-7 text-blue-600'} />
                </div>
                <span className={`text-xs font-medium uppercase tracking-wider ${isNight ? 'text-white/40' : 'text-slate-400'}`}>
                  {t('dashboard.total')}
                </span>
              </div>
              <div className={`text-4xl font-bold drop-shadow-md ${isNight ? 'text-white' : 'text-slate-800'}`}>
                {products?.toLocaleString() || 0}
              </div>
              <div className={`mt-1 text-sm ${isNight ? 'text-white/60' : 'text-slate-500'}`}>
                {t('dashboard.products')}
              </div>
            </div>

            <div className={cardClass}>
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl ${isNight ? 'bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/20' : 'bg-white/80 border border-slate-200'}`}>
                  <Users className={isNight ? 'w-7 h-7 text-emerald-300' : 'w-7 h-7 text-emerald-600'} />
                </div>
                <span className={`text-xs font-medium uppercase tracking-wider ${isNight ? 'text-white/40' : 'text-slate-400'}`}>
                  {t('dashboard.active')}
                </span>
              </div>
              <div className={`text-4xl font-bold drop-shadow-md ${isNight ? 'text-white' : 'text-slate-800'}`}>
                {employees?.total?.toLocaleString() || 0}
              </div>
              <div className={`mt-1 text-sm ${isNight ? 'text-white/60' : 'text-slate-500'}`}>
                {t('dashboard.employees')}
              </div>
              <div className={`mt-3 flex gap-4 text-xs ${isNight ? 'text-white/50' : 'text-slate-400'}`}>
                <span className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${isNight ? 'bg-blue-400 shadow-[0_0_6px_rgba(96,165,250,0.6)]' : 'bg-blue-500'}`} />
                  {t('dashboard.human')}: {employees?.human || 0}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${isNight ? 'bg-purple-400 shadow-[0_0_6px_rgba(192,132,252,0.6)]' : 'bg-purple-500'}`} />
                  {t('dashboard.ai')}: {employees?.ai || 0}
                </span>
              </div>
            </div>

            <div className={cardClass}>
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl ${isNight ? 'bg-amber-500/20 backdrop-blur-sm border border-amber-400/20' : 'bg-white/80 border border-slate-200'}`}>
                  <Truck className={isNight ? 'w-7 h-7 text-amber-300' : 'w-7 h-7 text-amber-600'} />
                </div>
                <span className={`text-xs font-medium uppercase tracking-wider ${isNight ? 'text-white/40' : 'text-slate-400'}`}>
                  {t('dashboard.total')}
                </span>
              </div>
              <div className={`text-4xl font-bold drop-shadow-md ${isNight ? 'text-white' : 'text-slate-800'}`}>
                {suppliers?.toLocaleString() || 0}
              </div>
              <div className={`mt-1 text-sm ${isNight ? 'text-white/60' : 'text-slate-500'}`}>
                {t('dashboard.suppliers')}
              </div>
            </div>
          </div>

          {/* Bar chart */}
          <div className={cardClass}>
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className={`w-5 h-5 ${isNight ? 'text-indigo-300' : 'text-indigo-500'}`} />
              <h2 className={`text-lg font-semibold ${isNight ? 'text-white/90' : 'text-slate-800'}`}>
                {t('dashboard.product_trend')}
              </h2>
            </div>

            {product_trend && product_trend.length > 0 ? (
              <div className="flex gap-2">
                <div className={`flex flex-col justify-between text-xs pb-8 w-10 shrink-0 ${isNight ? 'text-white/50' : 'text-slate-400'}`}>
                  <span className="text-right">{maxTrendCount}</span>
                  <span className="text-right">{Math.round(maxTrendCount / 2)}</span>
                  <span className="text-right">0</span>
                </div>
                <div className="flex-1">
                  <div className={`flex items-end gap-3 pt-1 min-h-[200px] border-l-2 border-b-2 pl-2 ${isNight ? 'border-white/20' : 'border-slate-300'}`}>
                    {product_trend.map((item, idx) => {
                      const barH = Math.max(Math.round((item.count / maxTrendCount) * 200), 2);
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-1 group">
                          <span className={`text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity ${isNight ? 'text-white/70' : 'text-slate-600'}`}>
                            {item.count}
                          </span>
                          <div
                            className="w-full max-w-[48px] rounded-t-md cursor-pointer relative transition-all duration-200 hover:brightness-125"
                            style={{
                              height: `${barH}px`,
                              background: 'linear-gradient(180deg, #818cf8 0%, #6366f1 50%, #4f46e5 100%)',
                              boxShadow: isNight
                                ? '0 0 16px rgba(99,102,241,0.5)'
                                : '0 0 12px rgba(99,102,241,0.3)',
                            }}
                          >
                            <div className={`absolute -top-8 left-1/2 -translate-x-1/2 text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10 border ${
                              isNight
                                ? 'bg-white/15 backdrop-blur-md text-white border-white/20'
                                : 'bg-white text-slate-700 border-slate-200 shadow-md'
                            }`}>
                              {item.date}: {item.count}
                            </div>
                          </div>
                          <span className={`text-[10px] mt-1 truncate max-w-full text-center ${isNight ? 'text-white/40' : 'text-slate-400'}`}>
                            {item.date.slice(5)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className={`py-12 text-center ${isNight ? 'text-white/40' : 'text-slate-400'}`}>
                {t('dashboard.no_trend_data')}
              </div>
            )}
          </div>
        </div>
          ) : activeGame === null ? (
            <GameMenu
              isNight={isNight}
              onSelectGame={setActiveGame}
              onClose={() => setShowContent(true)}
            />
          ) : (
            <div className="h-[calc(100vh-7rem)]">
              <SpaceShooterGame onClose={() => setActiveGame(null)} />
            </div>
          )}
        </div>
      </PageWrapper>
    </>
  );
};

export default DashboardPage;
